import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCompare } from "@/context/CompareContext";

export default function CompareBar() {
  const { compareList, removeFromCompare, clearCompare, openModal } = useCompare();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (compareList.length >= 2) {
      setVisible(true);
    } else if (compareList.length === 0) {
      setVisible(false);
    }
  }, [compareList.length]);

  const show = compareList.length >= 1;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ease-out ${
        show ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="bg-gray-900 border-t border-gray-700 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-4">
          {/* Label */}
          <div className="shrink-0">
            <p className="text-white text-sm font-bold whitespace-nowrap">
              Compare
              <span className="ml-1.5 bg-amber-500 text-white text-xs font-extrabold w-5 h-5 rounded-full inline-flex items-center justify-center">
                {compareList.length}
              </span>
            </p>
            <p className="text-gray-400 text-xs whitespace-nowrap">
              {compareList.length < 2 ? "Add 1 more to compare" : `${compareList.length} of 3 selected`}
            </p>
          </div>

          {/* Slots */}
          <div className="flex items-center gap-3 flex-1 overflow-x-auto">
            {[0, 1, 2].map((slot) => {
              const product = compareList[slot];
              if (product) {
                return (
                  <div
                    key={product.id}
                    className="flex items-center gap-2 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 shrink-0"
                  >
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded overflow-hidden shrink-0">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain p-0.5" />
                    </div>
                    <div className="max-w-[130px]">
                      <p className="text-white text-xs font-semibold truncate leading-tight">{product.name}</p>
                      <p className="text-amber-400 text-xs font-bold">${product.price.toLocaleString()}</p>
                    </div>
                    <button
                      onClick={() => removeFromCompare(product.id)}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-400 transition-colors cursor-pointer shrink-0 ml-1"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                );
              }
              return (
                <div
                  key={`empty-${slot}`}
                  className="flex items-center justify-center w-36 h-14 bg-gray-800/50 border border-gray-700 border-dashed rounded-lg shrink-0"
                >
                  <p className="text-gray-600 text-xs">+ Add product</p>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={clearCompare}
              className="text-gray-400 hover:text-gray-200 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap px-2 py-1.5"
            >
              Clear all
            </button>

            {/* Share — open /compare page with IDs in URL */}
            <button
              onClick={() => {
                if (compareList.length < 1) return;
                const ids = compareList.map((p) => p.id).join(",");
                navigate(`/compare?ids=${ids}`);
                if (!copied) {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/compare?ids=${ids}`
                  ).catch(() => {});
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }
              }}
              disabled={compareList.length < 1}
              className={`hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer border ${
                compareList.length >= 1
                  ? copied
                    ? "border-emerald-500 text-emerald-400 bg-emerald-900/30"
                    : "border-gray-600 text-gray-300 hover:border-amber-400 hover:text-amber-300"
                  : "border-gray-700 text-gray-600 cursor-not-allowed"
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={copied ? "ri-check-line" : "ri-share-line"}></i>
              </div>
              {copied ? "Copied!" : "Share"}
            </button>

            <button
              onClick={openModal}
              disabled={compareList.length < 2}
              className={`px-5 py-2.5 rounded-md text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                compareList.length >= 2
                  ? "bg-amber-500 hover:bg-amber-400 text-white"
                  : "bg-gray-700 text-gray-500 cursor-not-allowed"
              }`}
            >
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-list-check-2"></i>
                </div>
                Compare Now
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
